import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  InputAdornment,

} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Edit, Delete, Close, Save, Person, Email, Lock, AdminPanelSettings, ManageAccounts, Business, Add, Replay, ForwardToInbox } from "@mui/icons-material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import UserService from "../../api/services/UserService";
import { styled, alpha } from "@mui/material/styles";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Slide from "@mui/material/Slide";
import SocieteService from "../../api/services/SocieteService";

const UserDialog = ({ open, onClose, title, userData, setUserData, error, loadingSocietes, societes, onSave, saving, isEdit = false }) => {
  const theme = useTheme();
  const isGestionnaire = userData.role === "gestionnaire";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
          backdropFilter: "blur(20px)",
          border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
          boxShadow: `0 25px 50px ${alpha(theme.palette.common.black, 0.15)}`,
          m: { xs: 2, sm: 4 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, position: "relative" }}>
        <Box display="flex" alignItems="center" gap={2}>
          <SectionTitle component="div" sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" } }}>
            {title}
          </SectionTitle>
        </Box>
        <IconButton aria-label="fermer" onClick={onClose} sx={{ position: 'absolute', right: 16, top: 16, bgcolor: alpha(theme.palette.action.hover, 0.1), '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.2) } }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 4, px: { xs: 3, sm: 5 }, pb: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: "16px", fontSize: "0.95rem" }}>{error}</Alert>}

        {/* Nom complet */}
        <TextField
          label="Nom complet "
          placeholder="Jean Dupont"
          value={userData.nom || ""}
          onChange={(e) => setUserData({ ...userData, nom: e.target.value })}
          required
          fullWidth
          margin="dense"
          InputProps={{
            startAdornment: <InputAdornment position="start"><Person color="action" /></InputAdornment>,
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: "16px", bgcolor: alpha(theme.palette.background.paper, 0.7) } }}
        />

        {/* Email */}
        <TextField
          label="Adresse email "
          type="email"
          placeholder="jean.dupont@entreprise.com"
          value={userData.email || ""}
          onChange={(e) => setUserData({ ...userData, email: e.target.value })}
          required
          fullWidth
          margin="dense"
          InputProps={{
            startAdornment: <InputAdornment position="start"><Email color="action" /></InputAdornment>,
          }}
          sx={{ mt: 3, '& .MuiOutlinedInput-root': { borderRadius: "16px", bgcolor: alpha(theme.palette.background.paper, 0.7) } }}
        />


        {/* Rôle */}
        <FormControl fullWidth sx={{ mt: 3 }}>
          <InputLabel>Rôle dans la plateforme *</InputLabel>
          <Select
            value={userData.role || "gestionnaire"}
            label="Rôle dans la plateforme *"
            onChange={(e) => {
              const role = e.target.value;
              setUserData({ ...userData, role, societe_id: role === "admin" ? "" : userData.societe_id });
            }}

            sx={{ '& .MuiOutlinedInput-root': { borderRadius: "16px", bgcolor: alpha(theme.palette.background.paper, 0.7) } }}
          >
            <MenuItem value="admin"><Box display="flex" alignItems="center" gap={1}><AdminPanelSettings fontSize="small" /> Administrateur (accès total)</Box></MenuItem>
            <MenuItem value="gestionnaire"><Box display="flex" alignItems="center" gap={1}><ManageAccounts fontSize="small" /> Gestionnaire </Box></MenuItem>
          </Select>
        </FormControl>

        {/* Société */}
        {isGestionnaire && (
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel>Sociétés assignées *</InputLabel>
            <Select
              multiple
              value={userData.societe_ids || []}
              label="Sociétés assignées *"
              onChange={(e) => setUserData({ ...userData, societe_ids: e.target.value })}
              disabled={loadingSocietes}
              startAdornment={
                <InputAdornment position="start" sx={{ ml: 1 }}>
                  <Business />
                </InputAdornment>
              }
              renderValue={(selected) =>
                selected.length === 0
                  ? "Choisir des sociétés"
                  : societes
                    .filter((s) => selected.includes(s.id))
                    .map((s) => s.nom)
                    .join(", ")
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: "16px",
                  bgcolor: alpha(theme.palette.background.paper, 0.7),
                },
              }}
            >
              {loadingSocietes ? (
                <MenuItem disabled>Chargement...</MenuItem>
              ) : (
                societes.map((soc) => (
                  <MenuItem key={soc.id} value={soc.id}>
                    {/* ✅ Checkbox visuelle pour voir les sélections */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <input
                        type="checkbox"
                        readOnly
                        checked={(userData.societe_ids || []).includes(soc.id)}
                        style={{ accentColor: "#2a5fa9", width: 16, height: 16 }}
                      />
                      <Box>
                        <Typography fontWeight={600}>{soc.nom}</Typography>
                        {soc.siret && (
                          <Typography variant="caption" color="text.secondary">
                            SIRET: {soc.siret}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 4, pt: 2, justifyContent: "flex-end", gap: 2 }}>
        <Button
          onClick={onClose}
          size="large"
          sx={{
            color: "#d32f2f",
            fontWeight: 2,
            order: 2,
            "&:hover": {
              backgroundColor: alpha("#d32f2f", 0.1)
            }
          }}
        >
          Annuler
        </Button>

        <Button
          onClick={onSave}
          variant="contained"
          size="large"
          startIcon={<Save />}
          sx={{
            borderRadius: "18px",
            px: 5,
            py: 1.4,
            fontWeight: 700,
            fontSize: "1.05rem",
            textTransform: "none",
            background: "#000000",
            color: "#ffffff",
            boxShadow: `0 10px 30px ${alpha('#000000', 0.4)}`,
            "&:hover": {
              background: "#1a1a1a",
              transform: "translateY(-4px)",
              boxShadow: `0 15px 40px ${alpha('#000000', 0.5)}`,
            },
            transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
            order: 3,
            pointerEvents: saving ? "none" : "auto",
            opacity: saving ? 0.7 : 1,
          }}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};



const StyledCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha('#2a5fa9', 0.05)} 0%, ${alpha('#65b986', 0.05)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 20px 40px ${alpha('#2a5fa9', 0.1)}`,
    border: `1px solid ${alpha('#2a5fa9', 0.2)}`,
  },
  padding: '24px',
  minHeight: '75vh',
  [theme.breakpoints.down('md')]: {
    padding: '16px',
    minHeight: '70vh',
    borderRadius: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
    minHeight: '65vh',
    borderRadius: '8px',
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  borderRadius: '16px',
  overflow: 'hidden',
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 8px 32px ${alpha('#000000', 0.1)}`,
  '& .MuiDataGrid-columnHeaders': {
    background: `linear-gradient(90deg, 
    #2a5fa9 0%, 
    #3a7fb5 20%,
    #4a9fc0 40%, 
    #5ab8a0 60%,
    #70bd8d 80%, 
    #8fc68b 100%)`,
    '& .MuiTableCell-head': {
      color: '#ffffff',
      fontWeight: 600,
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: 'none',
      padding: '20px 16px',
      whiteSpace: 'nowrap',
    },
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: 'none',
    padding: '20px 16px',
    [theme.breakpoints.down('md')]: {
      fontSize: '0.75rem',
      padding: '12px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem',
      padding: '8px',
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
    padding: '16px',
    '&:nth-of-type(even)': {
      backgroundColor: alpha(theme.palette.action.hover, 0.02),
    },
    [theme.breakpoints.down('md')]: {
      padding: '12px',
      fontSize: '0.875rem',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '8px',
      fontSize: '0.75rem',
    },
  },
  '& .name-column--cell': {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "none",
    background: `linear-gradient(135deg, #fcfdffff 0%, #f6f9faff 100%)`,
    color: "#ffffff",
  },
  '& .MuiCheckbox-root': {
    color: `${theme.palette.primary.main} !important`,
  },
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.divider, 0.1),
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha('#2a5fa9', 0.3),
    borderRadius: '4px',
    '&:hover': {
      background: alpha('#2a5fa9', 0.5),
    },
  },
  [theme.breakpoints.down('md')]: {
    borderRadius: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '8px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(90deg, 
    #2a5fa9 0%, 
    #3a7fb5 20%,
    #4a9fc0 40%, 
    #5ab8a0 60%,
    #70bd8d 80%, 
    #8fc68b 100%)`,
  '& .MuiTableCell-head': {
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: 'none',
    padding: '20px 16px',
    whiteSpace: 'nowrap',
  },
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
  fontSize: '1.5rem',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.25rem',
    marginBottom: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
    marginBottom: '12px',
    gap: '8px',
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  background: alpha('#2a5fa9', 0.1),
  color: '#2a5fa9',
  '&:hover': {
    background: alpha('#2a5fa9', 0.2),
    transform: 'scale(1.1)',
  },
  '&:disabled': {
    background: alpha(theme.palette.action.disabled, 0.1),
    color: theme.palette.action.disabled,
  },
  transition: 'all 0.2s ease',
}));

const Team = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [statutFilter, setStatutFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDelete, setOpenDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState({
    id: "",
    role: ""
  });
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState({
    id: "",
    nom: "",
    email: "",
    role: "",
    current_role: "",
    societe_id: "",
  });
  const [openAdd, setOpenAdd] = useState(false);
  const [newUser, setNewUser] = useState({
    nom: "", email: "", role: "gestionnaire",
    societe_ids: [],
  });

  const [societes, setSocietes] = useState([]);
  const [loadingSocietes, setLoadingSocietes] = useState(false);

  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [societeFilter, setSocieteFilter] = useState("");

  const [savingEdit, setSavingEdit] = useState(false);
  const [savingAdd, setSavingAdd] = useState(false);

  useEffect(() => {
    const loadSocietes = async () => {
      setLoadingSocietes(true);
      try {
        const token = localStorage.getItem("token");
        const data = await SocieteService.getAll(token);
        setSocietes(data);
      } catch (err) {
        setSnackbarMessage("Erreur lors du chargement des sociétés.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoadingSocietes(false);
      }
    };
    loadSocietes();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      const loggedInUserEmail = (userData.email || "").toLowerCase();

      const { gestionnaires, admins } = await UserService.getAllUsers(token);

      const combinedRows = [...gestionnaires, ...admins]
        .filter((user) => (user.email || "").toLowerCase() !== loggedInUserEmail)
        .map((user) => ({
          id: `${user.role}-${user.id}`,
          realId: user.id,
          nom: user.nom,
          email: user.email,
          role: user.role,
          societes: user.societes || [],
          statut: user.statut || 0,
        }))

      setRows(combinedRows);
      setError("");
    } catch (error) {
      console.error("Erreur lors du chargement des utilisateurs :", error);
      setError(
        error.response?.data?.message || "Erreur lors du chargement des utilisateurs."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredRows = rows.filter((row) => {
    const matchesSearch = row.nom.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter ? row.role === roleFilter : true;
    const matchesSociete = societeFilter
      ? row.societes?.some((s) => s.id === societeFilter)
      : true; const matchesStatut = statutFilter !== "" ? row.statut == statutFilter : true;
    return matchesSearch && matchesRole && matchesSociete && matchesStatut;
  });

  const handleDeleteClick = (id, role) => {
    setUserToDelete({
      id: id,
      role: role
    });
    setOpenDelete(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await UserService.deleteUser(userToDelete.id, userToDelete.role, token);
      setRows(rows.filter((user) => user.realId !== userToDelete.id));
      setOpenDelete(false);
      setSnackbarSeverity("success");
      setSnackbarMessage("Utilisateur supprimé avec succès !");
      setSnackbarOpen(true);
      setError("");
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error.response?.data?.message || "Erreur lors de la suppression de l'utilisateur.");
      setSnackbarOpen(true);
    }
  };


  const handleSendEmail = async (userId) => {
    if (!userId) {
      setSnackbarMessage("ID utilisateur manquant.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token manquant. Veuillez vous reconnecter.");
      }

      await UserService.sendEmail(userId, token);

      setSnackbarMessage("Email envoyé avec succès !");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Erreur envoi email :", error);
      setSnackbarMessage(
        error.message || "Échec de l'envoi de l'email."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleEditClick = (row) => {
    setEditData({
      id: row.realId,
      nom: row.nom,
      email: row.email,
      role: row.role,
      current_role: row.role,
      societe_ids: row.societes?.map((s) => s.id) || [],
    });
    setOpenEdit(true);
    setError("");
  };

  const handleEditSave = async () => {
    if (!editData.nom.trim() || !editData.email.trim() || !editData.role) {
      setError("Les champs Nom complet, Email et Rôle sont requis.");
      return;
    }


    setSavingEdit(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      const payload = {
        nom: editData.nom.trim(),
        email: editData.email.trim(),
        role: editData.role,
        current_role: editData.current_role,
        ...(editData.role === "gestionnaire" && { societe_ids: editData.societe_ids }),
      };

      await UserService.updateUser(editData.id, payload, token);
      await fetchUsers();

      setSnackbarSeverity("success");
      setSnackbarMessage("Utilisateur modifié avec succès !");
      setSnackbarOpen(true);
      setOpenEdit(false);
    } catch (err) {
      setSnackbarSeverity("error");

      // Gestion des erreurs Laravel (validation 422)
      if (err?.response?.status === 422 && err?.response?.data?.errors) {
        const firstError = Object.values(err.response.data.errors)[0][0];
        setSnackbarMessage(firstError);
      } else if (err?.response?.data?.message) {
        setSnackbarMessage(err.response.data.message);
      } else {
        setSnackbarMessage(err.message || "Une erreur est survenue lors de la modification.");
      }

      setSnackbarOpen(true);
    } finally {
      setSavingEdit(false);
    }
  };


  const handleAddSave = async () => {
    // Vérification des champs obligatoires
    if (!newUser.nom.trim() || !newUser.email.trim() || !newUser.role.trim()) {
      setError("Tous les champs sont obligatoires.");
      return;
    }

    setSavingAdd(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token d'authentification introuvable. Veuillez vous reconnecter.");
      }

      // Préparation des données à envoyer
      const userData = {
        nom: newUser.nom.trim(),
        email: newUser.email.trim(),
        role: newUser.role.trim(),
        ...(newUser.role === "gestionnaire" && { societe_ids: newUser.societe_ids || [] }),
      };

      await UserService.addUser(userData, token);
      await fetchUsers();

      // Notification de succès
      setSnackbarSeverity("success");
      setSnackbarMessage("Utilisateur ajouté avec succès !");
      setSnackbarOpen(true);

      // Réinitialisation du formulaire
      setOpenAdd(false);
      setNewUser({ nom: "", email: "", role: "gestionnaire", societe_id: "" });
    } catch (err) {
      console.error("Erreur lors de l'ajout de l'utilisateur :", err);

      setSnackbarSeverity("error");

      if (err?.response?.status === 422 && err?.response?.data?.errors) {
        // Erreur de validation Laravel
        const firstError = Object.values(err.response.data.errors)[0][0];
        setSnackbarMessage(firstError);
      } else {
        setSnackbarMessage(err.message || "Une erreur est survenue lors de l'ajout de l'utilisateur.");
      }

      setSnackbarOpen(true);
    } finally {
      setSavingAdd(false);
    }
  };


  const columns = [
    { field: "nom", headerName: "Nom complet", flex: 1, cellClassName: "name-column--cell", minWidth: 120, sortable: false, },
    { field: "email", headerName: "Email", flex: 1.5, minWidth: 150, sortable: false, },
    { field: "role", headerName: "Rôle", flex: 1, minWidth: 100, sortable: false, },
    {
      field: "societes",
      headerName: "Sociétés",
      flex: 1.2,
      minWidth: 150,
      sortable: false,
      valueGetter: (params) =>
        params.row.societes?.map((s) => s.nom).join(", ") || "-",
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography sx={{ fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "statut",
      headerName: "Statut",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box

        >
          {params.value == 1 ? "Actif" : "Non actif"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 100,
      renderCell: ({ row }) => (
        <Box display="flex" gap="5px">
          <Tooltip title="Modifier">
            <StyledIconButton onClick={() => handleEditClick(row)} size="small">
              <Edit fontSize="small" />
            </StyledIconButton>
          </Tooltip>

          {/* Bouton Envoyer Email */}
          <Tooltip
            title={
              row.statut == 1
                ? "Utilisateur déjà actif"
                : "relancer l'email de connexion "
            }
          >
            <span> {/* span pour désactiver le tooltip si disabled */}
              <StyledIconButton
                onClick={() => row.statut == 0 && handleSendEmail(row.realId)}
                size="small"
                disabled={row.statut != 0}
                sx={{
                  color: row.statut == 1 ? "#9e9e9e" : "#2a5fa9",
                  background: row.statut == 1 ? alpha("#9e9e9e", 0.1) : alpha("#2a5fa9", 0.1),
                  '&:hover': {
                    background: row.statut == 1 ? alpha("#9e9e9e", 0.2) : alpha("#2a5fa9", 0.2),
                  },
                }}
              >
                <ForwardToInbox fontSize="small" />
              </StyledIconButton>
            </span>
          </Tooltip>

          <Tooltip title="Supprimer">
            <StyledIconButton
              onClick={() => handleDeleteClick(
                row.realId,
                row.role
              )}
              size="small"
              sx={{ color: 'rgb(211, 47, 47)' }}
            >
              <Delete fontSize="small" />
            </StyledIconButton>
          </Tooltip>


        </Box>
      ),
      sortable: false,
    },
  ];

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        minHeight: "100vh",
        borderRadius: { xs: "16px", md: "24px" },
        p: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Header title="Utilisateurs" subtitle="Gestion des comptes utilisateurs" />
      <StyledCard>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
          <Box display="flex" gap={2} flexWrap="wrap" sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
            <TextField
              label="Rechercher par nom"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                borderRadius: "8px",
                width: { xs: '100%', sm: '200px' },
              }}
              size="small"
            />
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                borderRadius: "8px",
                width: { xs: '100%', sm: '200px' },
              }}
              size="small"
            >
              <MenuItem value="">Tous les rôles</MenuItem>
              <MenuItem value="admin">Administrateur</MenuItem>
              <MenuItem value="gestionnaire">Gestionnaire</MenuItem>
            </Select>
            <Select
              value={societeFilter}
              onChange={(e) => setSocieteFilter(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                borderRadius: "8px",
                width: { xs: '100%', sm: '200px' },
              }}
              size="small"
            >
              <MenuItem value="">Toutes les sociétés</MenuItem>
              {societes.map((soc) => (
                <MenuItem key={soc.id} value={soc.id}>
                  {soc.nom}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={statutFilter}
              onChange={(e) => setStatutFilter(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                borderRadius: "8px",
                width: { xs: '100%', sm: '200px' },
              }}
              size="small"
            >
              <MenuItem value="">Tous les statuts</MenuItem>
              <MenuItem value="0">Non actif</MenuItem>
              <MenuItem value="1">Actif</MenuItem>
            </Select>

          </Box>
          <Button
            variant="contained"
            onClick={() => setOpenAdd(true)}
            sx={{
              borderRadius: '8px',
              background: `linear-gradient(135deg, #2a5fa9 0%, #4cbed8 100%)`,
              color: '#ffffff',
              '&:hover': {
                background: `linear-gradient(135deg, #4cbed8 0%, #65b986 100%)`,
                transform: 'scale(1.05)'
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Ajouter un utilisateur
          </Button>
        </Box>
        <Box sx={{ height: { xs: "60vh", sm: "70vh", md: "75vh" }, width: "100%" }}>
          <StyledDataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            disableRowSelectionOnClick
            density="comfortable"
            pageSizeOptions={[20]}
            pagination
            localeText={{
              noRowsLabel: searchTerm
                ? "Aucun utilisateur trouvé pour ce nom"
                : "Aucun utilisateur trouvé",
              noResultsOverlayLabel: "Aucun utilisateur trouvé",
            }}
            disableColumnSorting={true}
            disableColumnMenu={true}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                overflow: "visible",
                whiteSpace: "normal",
                lineHeight: "1.2",
              },
            }}
          />
        </Box>
      </StyledCard>

      {/* Dialog de suppression */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
            backdropFilter: "blur(10px)",
            borderRadius: "16px",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            m: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle>
          <SectionTitle>Supprimer Utilisateur</SectionTitle>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            Voulez-vous vraiment supprimer cet utilisateur ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button
            onClick={() => setOpenDelete(false)}
            sx={{
              color: theme.palette.error.main,
              "&:hover": { backgroundColor: alpha(theme.palette.error.main, 0.1) },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "8px",
              background: alpha(theme.palette.error.main, 0.8),
              "&:hover": { background: theme.palette.error.main, transform: "scale(1.05)" },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* === DIALOG D'ÉDITION AMÉLIORÉ === */}
      <UserDialog
        open={openEdit}
        onClose={() => { setOpenEdit(false); setError(""); }}
        title={`Modifier ${editData.role === "admin" ? "l'administrateur" : "le gestionnaire"}`}
        userData={editData}
        setUserData={setEditData}
        error={error}
        loadingSocietes={loadingSocietes}
        societes={societes}
        onSave={handleEditSave}
        saving={savingEdit}
        isEdit={true}
      />

      {/* === DIALOG D'AJOUT AMÉLIORÉ === */}
      <UserDialog
        open={openAdd}
        onClose={() => { setOpenAdd(false); setError(""); }}
        title="Ajouter un nouvel utilisateur"
        userData={newUser}
        setUserData={setNewUser}
        error={error}
        loadingSocietes={loadingSocietes}
        societes={societes}
        onSave={handleAddSave}
        saving={savingAdd}
        isEdit={false}

      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={Slide}
        sx={{ mt: 8 }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

    </Box>
  );
};

export default Team;